import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../../../auth/AuthContext";
import QuizExamQuestion from "./QuizExamQuestion";
import ConfirmDialogBox from "../../../confirmdiolog/ConfirmDiologBox";
import AlertService from "../../../../services/AlertService";
import StorageService from "../../../../services/StorageService";
import { ArrowLeft, ArrowRight } from "lucide-react";
import QuizService from "../../../../services/QuizService";
 
 
const QUESTION_STATUS_COLORS = {
  neutral: "bg-slate-200 dark:bg-slate-800 text-black dark:text-white",
  select: "bg-[#7B97B7] text-white",
  answered: "bg-[#62977D] text-white",
};
 
const QuizExam = () => {
  const navigate = useNavigate();
  const { videoId, topicId } = useParams();
  const authContext = useContext(AuthContext);
 
  const scrollRef = useRef(null);
 
  const [loading, setLoading] = useState(false);
  const [quizInfo, setQuizInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
 
  const cookieName = `VideoQuiz#${videoId}#${authContext.userId}`;
 

 
const loadQuizData = useCallback(async () => {
    try {
      setLoading(true);
 
      const response = await QuizService.GetAllQuizzesForExam(
        videoId,
        authContext,
      );
 
      if (response?.isSuccess && response.data) {
        const quizData = response.data;
 
        let savedQuestions = [];
        try {
          savedQuestions = JSON.parse(StorageService.getLocal(cookieName));
        } catch {}
 
        const mappedQuestions = quizData.questions.map((q) => {
        const saved = savedQuestions?.find((sq) => sq.id === q.id);
 
          return {
            id: q.id,
            question: q.question,
            isAnswered: saved?.isAnswered || false,
            options: q.options.map((o) => {
              const savedOpt = saved?.options?.find((so) => so.id === o.id);
              return {
                id: o.id,
                option: o.option,
                isAnswer: savedOpt?.isAnswer || false,
              };
            }),
          };
        });
 
        setQuizInfo(quizData);
        setQuestions(mappedQuestions);
        setTimeLeft((quizData.duration || 10) * 60);
 
        if (!quizData.startDateTime) {
          await QuizService.StartQuizExam(videoId, authContext);
        }
      } else {
        AlertService.error(response?.errorMessage || "Failed to load quiz");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error loading quiz:", error);
      AlertService.error("Failed to load quiz");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  },[authContext, videoId, cookieName, navigate]);
  
    useEffect(() => {
    if (videoId) {
      loadQuizData();
    }
  }, [videoId,loadQuizData]);
 
  useEffect(() => {
    if (!timeLeft || timeLeft <= 0 || isExpired) return;
 
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsExpired(true);
          setShowDialog(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
 
    return () => clearInterval(timer);
  }, [timeLeft, isExpired]);
 
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;
 
  const timerText = isExpired
    ? "Expired"
    : `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0",
      )}:${String(seconds).padStart(2, "0")}`;
 
  const changeHandler = (qid, answerId) => {
    setQuestions((prev) => {
      const updated = prev.map((q) =>
        q.id === qid
          ? {
              ...q,
              isAnswered: true,
              options: q.options.map((o) => ({
                ...o,
                isAnswer: o.id === answerId,
              })),
            }
          : q,
      );
 
      setTimeout(() => {
        StorageService.setLocal(cookieName, JSON.stringify(updated));
      }, 10);
 
      return updated;
    });
  };
 
  const answeredCount = questions.filter((q) => q.isAnswered).length;
  const totalQuestions = quizInfo?.totalMCQQuestions || questions.length;
  const answeredPercent =
    totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
 
  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [selectedIndex]);
 
  const isEmptyGuid = (id) => id === "00000000-0000-0000-0000-000000000000";
 
  const submitQuiz = async () => {
    try {
      setLoading(true);
 
      let finalQuestions = questions;
      try {
        const saved = JSON.parse(StorageService.getLocal(cookieName));
        if (saved && saved.length > 0) {
          finalQuestions = saved;
        }
      } catch {}
 
      const payload = {
        id: quizInfo?.id && !isEmptyGuid(quizInfo.id) ? quizInfo.id : undefined,
 
        userId: authContext.userId,
        videoId: videoId,
        videoName: quizInfo?.videoName || "",
        startDateTime: quizInfo?.startDateTime || new Date().toISOString(),
        totalQuestions: totalQuestions,
        duration: quizInfo?.duration || 0,
        quizTimeInMinutes: quizInfo?.duration || 0,
        isExamAdded: true,
        isExamStarted: true,
        isExamCompleted: true,
        Questions: finalQuestions.map((q, index) => ({
          id: q.id,
          videoId: videoId,
          question: q.question,
          hasGraceMark: false,
          isAnswered: q.isAnswered,
          orderBy: index + 1,
          options: q.options.map((opt, optIndex) => ({
            id: opt.id,
            questionId: q.id,
            orderBy: optIndex + 1,
            option: opt.option,
            isAnswer: opt.isAnswer,
          })),
        })),
      };
 
      const response = await QuizService.SaveQuiz(payload, authContext);
 
      if (response?.isSuccess) {
        AlertService.success("Quiz submitted successfully!");
        StorageService.deleteLocal(cookieName);
        navigate(
          `/knowledge-management/topics/${topicId}/video/${videoId}?quizCompleted=true`,
          { replace: true },
        );
      } else {
        AlertService.error(response?.errorMessage || "Failed to submit quiz");
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
      AlertService.error("Failed to submit quiz");
    } finally {
      setLoading(false);
    }
  };
 

  if (loading && !quizInfo) {
    return (
      <div className="w-full pt-4 animate-pulse">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-2 max-h-[82vh] overflow-y-auto space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="rounded-md py-6 bg-slate-200 dark:bg-slate-700"
              ></div>
            ))}
          </div>
          <div className="col-span-10">
            <div className="flex flex-wrap gap-6 items-center mb-10">
              <div className="w-[120px] h-[120px] rounded-full border-4 border-slate-300 dark:border-slate-600 bg-slate-200 dark:bg-slate-700"></div>
              <div className="space-y-4">
                <div className="h-6 w-60 bg-slate-200 dark:bg-slate-700 rounded"></div>
                <div className="h-5 w-40 bg-slate-200 dark:bg-slate-700 rounded"></div>
              </div>
              <div className="ml-auto w-full md:w-1/3 flex flex-col items-end space-y-3">
                <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded"></div>
                <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded"></div>
                <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded"></div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="h-10 w-3/4 bg-slate-200 dark:bg-slate-700 rounded"></div>

              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-8 w-full bg-slate-200 dark:bg-slate-700 rounded"
                ></div>
              ))}
            </div>
            <div className="flex pt-6 items-center gap-4">
              <div className="h-10 w-28 bg-slate-200 dark:bg-slate-700 rounded"></div>
              <div className="ml-auto h-10 w-28 bg-slate-200 dark:bg-slate-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!quizInfo) {
    return null;
  }
 
  return (
    <Fragment>
      <div className="w-full px-4 mt-4">
        {/* DESKTOP */}
        <div className="hidden md:grid grid-cols-12 gap-4">
          <div className="col-span-2 max-h-[82vh] overflow-y-auto space-y-2">
            {questions.map((q, i) => {
              const isActive = i === selectedIndex;
 
              let statusClass = QUESTION_STATUS_COLORS.neutral;
              if (q.isAnswered) statusClass = QUESTION_STATUS_COLORS.answered;
              if (isActive) statusClass = QUESTION_STATUS_COLORS.select;
 
              return (
                <div
                  key={q.id}
                  ref={isActive ? scrollRef : null}
                  onClick={() => setSelectedIndex(i)}
                  className={`cursor-pointer rounded-md py-3 text-center text-base border ${statusClass}`}
                >
                  Question {i + 1}
                </div>
              );
            })}
          </div>
 
          {/* RIGHT CONTENT */}
          <div className="col-span-10">
            {/* HEADER */}
            <div className="flex gap-6 items-center mb-8 flex-nowrap w-full">
              {/* TIMER */}
              <div
                className={`w-[120px] h-[120px] flex items-center justify-center rounded-full border-4 shrink-0 ${
                  isExpired
                    ? "border-red-500 text-red-500"
                    : "border-orange-400 text-orange-400"
                } font-bold text-lg`}
              >
                {timerText}
              </div>
 
              {/* VIDEO INFO */}
              <div className="flex-1">
                <div className="font-semibold text-lg text-slate-800 dark:text-slate-200">
                  Video: {quizInfo.videoName}
                </div>
              </div>
 
              {/* PROGRESS */}
              <div className="flex items-center gap-3">
                <span className="text-slate-700 dark:text-slate-300 whitespace-nowrap">
                  {answeredPercent === 100 ? "Completed" : "Progress"}
                </span>
 
                <div className="w-64 bg-gray-200 dark:bg-gray-700 rounded h-2">
                  <div
                    className={`h-2 rounded ${
                      answeredPercent < 25
                        ? "bg-red-500"
                        : answeredPercent < 50
                          ? "bg-yellow-500"
                          : answeredPercent < 75
                            ? "bg-blue-500"
                            : "bg-green-600"
                    }`}
                    style={{ width: `${answeredPercent}%` }}
                  />
                </div>
 
                <span className="text-slate-700 dark:text-slate-300 whitespace-nowrap">
                  {answeredCount}/{totalQuestions}
                </span>
              </div>
            </div>
 
            {/* QUESTION */}
            {questions[selectedIndex] && (
              <QuizExamQuestion
                isExpired={isExpired}
                index={selectedIndex}
                input={questions[selectedIndex]}
                changeHandler={changeHandler}
              />
            )}
 
            {/* NAVIGATION */}
            <div className="flex items-center pt-6">
              {selectedIndex > 0 && (
                <button
                  onClick={() => setSelectedIndex((i) => i - 1)}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
                >
                  <ArrowLeft size={18} /> Previous
                </button>
              )}
 
              {selectedIndex < questions.length - 1 ? (
                <button
                  onClick={() => setSelectedIndex((i) => i + 1)}
                  disabled={loading}
                  className="ml-auto flex items-center gap-2 px-8 py-2 rounded-md bg-[#0e3a46] text-white hover:bg-[#0b2f39]"
                >
                  Next <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  onClick={() => setShowDialog(true)}
                  disabled={loading}
                  className="ml-auto px-6 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
                >
                  {loading ? "Submitting..." : "Submit Quiz"}
                </button>
              )}
            </div>
 
            {/* LEGEND */}
            <div className="flex justify-center items-center gap-8 mt-8">
              <div className="flex items-center gap-2">
                <div
                  className="w-5 h-5 rounded"
                  style={{ backgroundColor: "#7B97B7" }}
                ></div>
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Select
                </span>
              </div>
 
              <div className="flex items-center gap-2">
                <div
                  className="w-5 h-5 rounded"
                  style={{ backgroundColor: "#62977D" }}
                ></div>
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Answered
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
 
      {/* CONFIRM DIALOG */}
      {isExpired ? (
        <ConfirmDialogBox data-testid="confirm-dialog"
          visible={showDialog}
          onHide={() => {}}
          onConfirm={() => {
            setShowDialog(false);
            submitQuiz();
          }}
          title={
            <>
              🕒 Your quiz time has expired. <br />
              Please click <strong>"Submit"</strong> to save your responses.
              <br />
              <small className="text-muted">
                Unsaved answers will not be recorded.
              </small>
            </>
          }
          confirmLabel="Submit"
          cancelLabel={null}
          hideCloseIcon
        />
      ) : (
        <ConfirmDialogBox
          visible={showDialog}
          onHide={() => setShowDialog(false)}
          onConfirm={() => {
            setShowDialog(false);
            submitQuiz();
          }}
          title={
            <>
              Are you sure you want to submit the <strong>quiz</strong>? <br />
              <small className="text-muted">
                Once submitted, you cannot make any changes.
              </small>
            </>
          }
          confirmLabel="Submit"
          cancelLabel="Cancel"
        />
      )}
    </Fragment>
  );
};
 
export default QuizExam;